/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { MessageService } from '@src/app/shared/message.service';
import { Txn } from '@src/app/model/txn';
import { Field } from '@src/app/shared/field/Field';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { Cycle } from '@src/app/model/cycle';
import { Observable } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { BankAccountService } from '@src/app/modules/txn/bank-account.service';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { Action } from '@src/app/model/action';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PreferredSupplierPageNavRoute } from '@src/app/routes/preferred-supplier-page-nav';

export class TxnImportChecker {
    invoiceCount: number;
    invoices: Txn[];
    receiptCount: number;
    receipts: Txn[];
    creditCount: number;
    credits: Txn[];
    outstanding: number;
    actions: Action[];
}

@Injectable({
    providedIn: 'root'
})
export class TxnService extends AbstractHttpService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);
    protected unitSvc = inject(UnitService);
    personSvc = inject(PersonService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    protected bCodeSvc = inject(BCodeService);
    protected bankAccountSvc = inject(BankAccountService);
    protected scheduleSvc = inject(ScheduleService);


    protected get baseUrl(): string {
        return this.getAjaxPath('txns');
    }
    protected cache: Txn[];
    protected typeString = 'Txn';

    getTypeField() {
        return FormPicklistComponent.make('Type', 'typeId', 'type', { items: Txn.TYPES }, { readonly: true });
    }

    getFields(): Field[] {

        return [
            FieldMaker.id(),
            FieldMaker.rev(),
            FormTextComponent.make('Reference', 'myRefNr', { readonly: true, allowFiltering: false }),
            FormTextComponent.make('RefNr', 'refNr', { readonly: true }),
            FormPicklistComponent.make('Ledger', 'ledgerId', 'ledger', { items: Txn.LEDGERS }),
            this.getTypeField(),
            FormDateComponent.make('Transaction Date', 'txnDate', { cellOpts: { width: '2%' }, formColumn: 2 }),
            FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle', { service: this.cycleSvc }, { formColumn: 2 }),
            FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod', { service: this.periodSvc },
                { formColumn: 2, refresh: (o: Cycle, control: AppPicklistControl) => { if (o) { control.field.setPicklistItems(o.periods); control.setValue(null); } }, }),
            FormPicklistComponent.make('Account', 'bCodeId', 'bCode', { service: this.bCodeSvc }, { formColumn: 3 }),
            FormNavLinkComponent.makeDetail('Unit', 'unitName', 'myUnitId', UnitPageNavRoute),
            FormNavLinkComponent.makeDetail('Supplier', 'supplierName', 'mySupplierId', PreferredSupplierPageNavRoute),
            FormNumberComponent.make("Debit", "debit", { format: 'currency', width: 9, formatParms: '1.2-2' }, { formColumn: 4 }),
            FormNumberComponent.make("Credit", "credit", { format: 'currency', width: 9, formatParms: '1.2-2' }, { formColumn: 4 }),
            FormNumberComponent.make('Total Value', 'totalValue', { format: 'currency', width: 9, formatParms: '1.0-0' }, {
                visible: Field.noShow, calculateValue: o => ((o as Txn).debit - (o as Txn).credit), allowFiltering: false,
            }),
            FormTextAreaComponent.make('Notes', 'notes', { maxLength: 10 }),
            FormTextAreaComponent.make('Parent Notes', 'parentNotes', {maxLength: 10}),

            new GridField({
                field:
                    { label: 'Details', value: 'details', visible: Field.formOnly, formRow: 2 },
                rowFactory: () => [
                    FieldMaker.id({ label: 'Ref', visible: { phone: true, computer: true, form: true }, footer: { text: 'Totals', style: 'text-align: right' } }),
                    FormPicklistComponent.make('Account', 'bCodeId', 'bCode', { service: this.bCodeSvc },
                        { cellOpts: { width: '50%' } }
                    ),
                    FormNumberComponent.make("Debit", "debit", { format: 'currency', width: 9, formatParms: '1.2-2' }),
                    FormNumberComponent.make("Credit", "credit", { format: 'currency', width: 9, formatParms: '1.2-2' }),
                    FieldMaker.notes(),
                    FieldMaker.rev(),
                    FieldMaker.deleteGridRow()
                ],
                hasFooter: true
            }),
        ]
    }

    getImportChecks(): Observable<TxnImportChecker> {
        return this.http.get<TxnImportChecker>(this.baseUrl + '/importChecks').pipe(first()).pipe(
            map((data: TxnImportChecker) => {
                return data;
            }),
            catchError(this.handleError<TxnImportChecker>('get' + this.typeString, null))
        );
    }
}
